import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, Car, FileText, Receipt, Brain, User2,
  LogOut, Menu, X,
} from "lucide-react";

const NAV = [
  { to: "/",            label: "Inicio",           icon: LayoutDashboard, end: true, testid: "nav-home" },
  { to: "/clientes",    label: "Clientes",         icon: Users,           testid: "nav-clientes" },
  { to: "/expedientes", label: "Expedientes",      icon: Car,             testid: "nav-expedientes" },
  { to: "/presupuestos",label: "Presupuestos",     icon: FileText,        testid: "nav-presupuestos" },
  { to: "/facturas",    label: "Facturas",         icon: Receipt,         testid: "nav-facturas" },
  { to: "/ia",          label: "Inteligencia (IA)",icon: Brain,           testid: "nav-ia" },
  { to: "/perfil",      label: "Mi Perfil",        icon: User2,           testid: "nav-perfil" },
];

function SidebarContent({ onNavigate, user, onLogout }) {
  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <img
          src="/assets/logo.png"
          alt="Quilles Varilleros"
          className="w-32 h-auto"
          data-testid="brand-logo"
        />
      </div>
      <nav className="px-3 py-2 flex-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "active" : ""} min-h-[44px]`
            }
          >
            <n.icon size={18} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-[var(--rule)] pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="text-xs text-zinc-500 mb-2 truncate" data-testid="user-email">
          {user?.email}
        </div>
        <button
          data-testid="logout-button"
          onClick={onLogout}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-md bg-zinc-100 hover:bg-zinc-200 text-sm transition min-h-[44px]"
        >
          <LogOut size={14} /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Cierra el drawer cuando cambia la ruta (al navegar desde el menú).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Cuando el drawer está abierto en móvil, bloqueamos el scroll del body.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  // Título corto de la sección actual para el topbar móvil.
  const current = NAV.find((n) =>
    n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
  );
  const title = current?.label || "CRM PDR";

  const doLogout = async () => { await logout(); nav("/login"); };

  // Etiqueta automática de columnas para tablas .tbl en móvil.
  // Cada vez que cambia la ruta o el DOM, recorremos las tablas .tbl y
  // copiamos el texto de cada <th> al atributo data-label de la <td>
  // correspondiente. Así las celdas se muestran con su etiqueta en móvil sin
  // tocar el JSX de cada página. El estilo lo gestiona index.css.
  const mainRef = useRef(null);
  useLayoutEffect(() => {
    const apply = () => {
      if (!mainRef.current) return;
      const tables = mainRef.current.querySelectorAll("table.tbl");
      tables.forEach((tbl) => {
        const ths = Array.from(tbl.tHead?.rows[0]?.cells || []);
        const labels = ths.map((th) => th.textContent.trim());
        Array.from(tbl.tBodies || []).forEach((tb) => {
          Array.from(tb.rows).forEach((row) => {
            Array.from(row.cells).forEach((td, i) => {
              const label = labels[i];
              if (label && !td.getAttribute("data-label")) {
                td.setAttribute("data-label", label);
              }
            });
          });
        });
      });
    };
    apply();
    // Reaplicamos cuando el contenido cambia (filtros, paginación, etc.).
    const obs = new MutationObserver(apply);
    if (mainRef.current) {
      obs.observe(mainRef.current, { childList: true, subtree: true });
    }
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar fijo en desktop */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-[var(--rule)] bg-white flex-col">
        <SidebarContent user={user} onLogout={doLogout} />
      </aside>

      {/* Topbar móvil */}
      <header
        className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-3 py-2 border-b border-[var(--rule)] bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70"
        style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top))" }}
      >
        <button
          aria-label="Abrir menú"
          data-testid="mobile-menu-button"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-zinc-100 active:bg-zinc-200"
        >
          <Menu size={22} />
        </button>
        <div className="flex-1 min-w-0 text-center">
          <div className="font-display text-lg leading-none truncate">{title}</div>
        </div>
        {/* Espaciador del mismo tamaño que el botón para centrar el título. */}
        <div className="w-11 h-11" aria-hidden="true" />
      </header>

      {/* Overlay + drawer móvil */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white border-r border-[var(--rule)] shadow-xl transform transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú principal"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center justify-end px-2 pt-2">
          <button
            aria-label="Cerrar menú"
            data-testid="mobile-menu-close"
            onClick={() => setDrawerOpen(false)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-md hover:bg-zinc-100 active:bg-zinc-200"
          >
            <X size={22} />
          </button>
        </div>
        <SidebarContent
          user={user}
          onLogout={doLogout}
          onNavigate={() => setDrawerOpen(false)}
        />
      </aside>

      {/* Contenido principal */}
      <main ref={mainRef} className="flex-1 min-w-0 overflow-x-hidden">
        <div
          className="w-full max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 animate-fade-up"
          style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
